import {H2, H3, P, PNoMargin} from '@/typography';
import {useTranslations} from 'next-intl';
import {SectionPadding} from '@/section';
import Container from '@/container';
import {Row, Col, Grid} from '@/grid';
import {withBgNeutral200, withNeutral700, withNeutral800} from '@/hocs/withColors';
import {withMarginBottomXl, withMarginBottomXlToSm} from '@/hocs/withSpacing';
import {withBoldWeight, withHeadingEyebrow, withHomeSubP} from '@/hocs/withFontType';
import {withStyles} from '@/hocs/withStyles';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';
import HighlightBox from '@/ui/highlight-box';
import PictureWrapper from '@/ui/images-wrapper';
import styles from './styles.module.scss';

const imgPath = '/static/images/home/talent-sourcing/';
const atsImgPath = '/static/images/home/applicant-tracking/';

const sourcingSolutionResponsiveImages = {
  desktopImageUrl: 'sourcing-solution-desktop',
  mobileImageUrl: 'sourcing-solution-mobile'
};

const crmDatabaseResponsiveImages = {
  desktopImageUrl: 'crm-database-desktop',
  mobileImageUrl: 'crm-database-mobile'
};

const repositionsResponsiveImages = {
  desktopImageUrl: 'repositions-desktop',
  mobileImageUrl: 'repositions-mobile'
};

const autoScheduleResponsiveImages = {
  desktopImageUrl: 'auto-schedule-desktop',
  mobileImageUrl: 'auto-schedule-mobile'
};

const Neutral200Section = withBgNeutral200(SectionPadding);
const EyebrowNeutral700 = withNeutral700(withHeadingEyebrow(P));
const SubPNeutral800 = withNeutral800(withHomeSubP(PNoMargin));
const TitleBlock = withMarginBottomXl('div');
const DivMargined = withMarginBottomXlToSm('div');
const DivSectionGap = withStyles('div', styles, 'section-gap');
const BoldSpan = withBoldWeight('span');

const TalentSourcingSection = () => {
  const t = useTranslations('homepage.talentSourcing');
  const tAts = useTranslations('homepage.applicantTracking');
  return (
    <Neutral200Section>
      <Container>
        <Grid fluid>
          <Row center="xs">
            <Col xs={12} md={10}>
              <EyebrowNeutral700>{t('eyebrow')}</EyebrowNeutral700>
              <H2>{t('title')}</H2>
              <TitleBlock>
                <SubPNeutral800>{t('subtitle')}</SubPNeutral800>
              </TitleBlock>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <DivMargined>
                <PictureWrapper
                  imgPath={imgPath}
                  responsiveImages={sourcingSolutionResponsiveImages}
                  imageTypeFallback="jpg"
                  altImage={t('imageAlt')}
                  rounded
                />
              </DivMargined>

              <DivSectionGap>
                <GridLayout Col={3} lgCol={2} autoRows>
                  <GridItem>
                    <BoxGrid>
                      <H3>{t('box1.title')}</H3>
                      <PNoMargin>{t('box1.description')}</PNoMargin>
                    </BoxGrid>
                  </GridItem>
                  <GridItem>
                    <BoxGrid backgroundColor="var(--yellow100)">
                      <H3>{t('box2.title')}</H3>
                      <PNoMargin>
                        {t.rich('box2.description', {
                          span: chunks => <BoldSpan>{chunks}</BoldSpan>
                        })}
                      </PNoMargin>
                    </BoxGrid>
                  </GridItem>
                  <GridItem>
                    <BoxGrid>
                      <H3>{t('box3.title')}</H3>
                      <PNoMargin>{t('box3.description')}</PNoMargin>
                    </BoxGrid>
                  </GridItem>
                </GridLayout>
              </DivSectionGap>

              <DivMargined>
                <GridLayout Col={3} lgCol={2} autoRows>
                  <GridItem Order={1}>
                    <BoxGrid>
                      <H3>{tAts('box1.title')}</H3>
                      <PNoMargin>{tAts('box1.description')}</PNoMargin>
                    </BoxGrid>
                  </GridItem>
                  <GridItem Order={3} lgOrder={2}>
                    <BoxGrid
                      imgPath={atsImgPath}
                      imageUrl="evaluation-tools"
                      imageTypeFallback="jpg"
                      altImage={tAts('box2.imageAlt')}
                      flipEnabled
                      minHeight={255}
                      width={376}
                      height={344}>
                      <H3>{tAts('box2.title')}</H3>
                      <P>{tAts('box2.description')}</P>
                    </BoxGrid>
                  </GridItem>
                  <GridItem Order={2} lgOrder={3} Col={2} Row={2}>
                    <div className={styles['crm-database-image']}>
                      <PictureWrapper
                        imgPath={atsImgPath}
                        responsiveImages={crmDatabaseResponsiveImages}
                        imageTypeFallback="jpg"
                        altImage={tAts('imageAlt')}
                        rounded
                      />
                    </div>
                  </GridItem>
                </GridLayout>
              </DivMargined>

              {/* 2nd & 3rd row */}
              <GridLayout Col={2} autoRows>
                <GridItem>
                  <PictureWrapper
                    imgPath={atsImgPath}
                    responsiveImages={repositionsResponsiveImages}
                    imageTypeFallback="jpg"
                    altImage={tAts('repositionsImgAlt')}
                    className={styles['row-image']}
                    rounded
                  />
                </GridItem>
                <GridItem>
                  <PictureWrapper
                    imgPath={atsImgPath}
                    responsiveImages={autoScheduleResponsiveImages}
                    imageTypeFallback="jpg"
                    altImage={tAts('autoScheduleImgAlt')}
                    className={styles['row-image']}
                    rounded
                  />
                </GridItem>
                <GridItem>
                  <BoxGrid>
                    <H3>{tAts('box3.title')}</H3>
                    <PNoMargin>{tAts('box3.description')}</PNoMargin>
                  </BoxGrid>
                </GridItem>
                <GridItem>
                  <BoxGrid>
                    <H3>{tAts('box4.title')}</H3>
                    <PNoMargin>{tAts('box4.description')}</PNoMargin>
                  </BoxGrid>
                </GridItem>
                <GridItem />
              </GridLayout>

              {/* 4th row */}
              <DivMargined>
                <GridLayout Col={3} lgCol={2} autoRows noStackMargin>
                  <GridItem>
                    <BoxGrid
                      imgPath={atsImgPath}
                      imageUrl="candidate-experience"
                      imageTypeFallback="jpg"
                      altImage={tAts('box5.imageAlt')}
                      flipEnabled
                      width={376}
                      height={364}>
                      <H3>{tAts('box5.title')}</H3>
                      <P>{tAts('box5.description')}</P>
                    </BoxGrid>
                  </GridItem>
                  <GridItem>
                    <BoxGrid
                      imgPath={atsImgPath}
                      imageUrl="flexible-workflows"
                      imageTypeFallback="jpg"
                      altImage={tAts('box6.imageAlt')}
                      flipEnabled
                      width={376}
                      height={364}>
                      <H3>{tAts('box6.title')}</H3>
                      <P>{tAts('box6.description')}</P>
                    </BoxGrid>
                  </GridItem>
                  <GridItem>
                    <BoxGrid
                      imgPath={atsImgPath}
                      imageUrl="offer-management"
                      imageTypeFallback="jpg"
                      altImage={tAts('box7.imageAlt')}
                      flipEnabled
                      width={376}
                      height={364}>
                      <H3>{tAts('box7.title')}</H3>
                      <P>{tAts('box7.description')}</P>
                    </BoxGrid>
                  </GridItem>
                </GridLayout>
              </DivMargined>

              <HighlightBox backgroundColor="var(--yellow100)">
                <PNoMargin>
                  {tAts.rich('infoBox.description', {
                    bold: chunks => <BoldSpan>{chunks}</BoldSpan>
                  })}
                </PNoMargin>
              </HighlightBox>
            </Col>
          </Row>
        </Grid>
      </Container>
    </Neutral200Section>
  );
};

export default TalentSourcingSection;
